import React, { useState } from 'react';
import { Upload, Image as ImageIcon, Link, Info, Maximize2 } from 'lucide-react';
import { getUploadUrlWithQuota } from '../../services/uploadWithQuota';
import ColorSwatchPicker from './ColorSwatchPicker';

interface RealSizeImageBlockProps {
  data: any;
  onChange: (data: any) => void;
  onDelete?: () => void;
  isPreview?: boolean;
}

const DEFAULT_ACCENT_COLOR = '#a855f7';

function hexToRgba(hex: string, alpha: number): string {
  const clean = (hex || '').replace('#', '');
  if (clean.length !== 6) return `rgba(168,85,247,${alpha})`;
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

const RealSizeImageBlock: React.FC<RealSizeImageBlockProps> = ({ data, onChange, isPreview = false }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const accentColor = data?.accentColor || DEFAULT_ACCENT_COLOR;

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('กรุณาเลือกไฟล์รูปภาพเท่านั้น');
      return;
    }

    // Check file size (max 20MB for real-size images)
    if (file.size > 20 * 1024 * 1024) {
      alert('ไฟล์มีขนาดใหญ่เกิน 20MB');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    
    try {
      console.log('[RealSizeImageBlock] Uploading image:', file.name, file.size);
      
      // Upload to Cloudflare R2 via Worker
      const result = await getUploadUrlWithQuota(
        file.name,
        file.type,
        file.size,
        file,
        (progress) => {
          setUploadProgress(progress);
          console.log(`Upload progress: ${progress}%`);
        }
      );
      
      console.log('[RealSizeImageBlock] Upload successful:', result);
      
      onChange({
        ...data,
        imageUrl: result.publicUrl,
        alt: file.name,
        width: '100%',
        height: 'auto',
        fileName: file.name,
        fileSize: file.size,
        isUrl: false
      });
      
      alert('✅ อัปโหลดรูปภาพขนาดจริงสำเร็จ!');
    } catch (error: any) {
      console.error('Error uploading image:', error);
      alert(error.message || 'เกิดข้อผิดพลาดในการอัปโหลดรูปภาพ');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleUrlSubmit = () => {
    if (!imageUrl.trim()) {
      alert('กรุณาใส่ URL รูปภาพ');
      return;
    }

    // Basic URL validation
    try {
      new URL(imageUrl);
    } catch {
      alert('URL ไม่ถูกต้อง กรุณาตรวจสอบอีกครั้ง');
      return;
    }

    onChange({
      ...data,
      imageUrl: imageUrl,
      alt: 'Image from URL',
      width: '100%',
      height: 'auto',
      fileName: 'Image from URL',
      isUrl: true
    });
    setImageUrl('');
    setShowUrlInput(false);
  };

  if (!data?.imageUrl) {
    return (
      <div className="rounded-lg p-8" style={{ border: `2px dashed ${hexToRgba(accentColor, 0.5)}`, backgroundColor: hexToRgba(accentColor, 0.05) }}>
        {!isPreview && (
          <div className="mb-4 flex items-center gap-2">
            <ColorSwatchPicker
              compact
              color={accentColor}
              fallback={DEFAULT_ACCENT_COLOR}
              onChange={(hex) => onChange({ ...data, accentColor: hex })}
            />
            <span className="text-sm font-bold text-gray-700">สีธีมกล่องอัปโหลด</span>
          </div>
        )}
        {!showUrlInput ? (
          <>
            <label className="flex flex-col items-center cursor-pointer">
              <Maximize2 className="w-16 h-16 mb-4" style={{ color: accentColor }} />
              <div className="flex items-center gap-2 mb-2">
                <span className="font-medium" style={{ color: accentColor }}>คลิกเพื่ออัปโหลดรูปภาพขนาดจริง</span>
                <div className="relative">
                  <Info className="w-4 h-4 cursor-help peer" style={{ color: hexToRgba(accentColor, 0.7) }} />
                  <div className="absolute left-0 top-6 w-64 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 invisible peer-hover:opacity-100 peer-hover:visible transition-all duration-200 z-10 shadow-xl pointer-events-none">
                    <p className="font-semibold mb-1">รูปภาพขนาดจริง (Premium)</p>
                    <p className="text-gray-300">รูปภาพจะแสดงขนาดจริงและปรับตามขนาดหน้าจอโดยอัตโนมัติ เหมาะสำหรับภาพความละเอียดสูง</p>
                  </div>
                </div>
              </div>
              <span className="text-sm mb-1" style={{ color: hexToRgba(accentColor, 0.85) }}>รองรับไฟล์ JPG, PNG, GIF, WebP (สูงสุด 20MB)</span>
              <span className="text-xs" style={{ color: hexToRgba(accentColor, 0.75) }}>แนะนำ: ใช้ภาพความละเอียดสูงเพื่อผลลัพธ์ที่ดีที่สุด</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                disabled={uploading}
              />
            </label>
            <div className="mt-4 text-center">
              <button
                onClick={() => setShowUrlInput(true)}
                className="text-sm flex items-center gap-2 mx-auto"
                style={{ color: accentColor }}
              >
                <Link className="w-4 h-4" />
                หรือวางลิงก์รูปภาพ
              </button>
            </div>
          </>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-col items-center">
              <Link className="w-16 h-16 mb-4" style={{ color: accentColor }} />
              <span className="font-medium mb-2" style={{ color: accentColor }}>วางลิงก์รูปภาพขนาดจริง</span>
            </div>
            <div className="flex gap-2">
              <input
                type="url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
                style={{ borderColor: hexToRgba(accentColor, 0.5) }}
                onKeyPress={(e) => e.key === 'Enter' && handleUrlSubmit()}
              />
              <button
                onClick={handleUrlSubmit}
                className="px-6 py-2 text-white rounded-lg transition-colors"
                style={{ backgroundColor: accentColor }}
              >
                เพิ่ม
              </button>
            </div>
            <button
              onClick={() => {
                setShowUrlInput(false);
                setImageUrl('');
              }}
              className="text-sm mx-auto block"
              style={{ color: accentColor }}
            >
              ← กลับไปอัปโหลดไฟล์
            </button>
          </div>
        )}
        {uploading && (
          <div className="mt-4">
            <div className="flex items-center justify-center mb-2">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: accentColor }}></div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
              <div
                className="h-2 rounded-full transition-all duration-200 ease-out"
                style={{ width: `${uploadProgress}%`, backgroundColor: accentColor }}
              ></div>
            </div>
            <p className="text-sm text-center" style={{ color: accentColor }}>กำลังอัปโหลด... {uploadProgress}%</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="relative rounded-lg overflow-hidden">
        {/* Upload Progress Overlay */}
        {uploading && (
          <div className="absolute inset-0 bg-white/95 flex flex-col items-center justify-center z-10">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mb-4"></div>
            <div className="w-64 bg-gray-200 rounded-full h-2 mb-2">
              <div 
                className="bg-purple-500 h-2 rounded-full transition-all duration-200 ease-out"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <p className="text-sm text-purple-600 font-semibold">กำลังอัปโหลดรูปภาพขนาดจริง... {uploadProgress}%</p>
            <p className="text-xs text-gray-500 mt-1">กรุณารอสักครู่</p>
          </div>
        )}
        
        <img
          src={data.imageUrl}
          alt={data.alt || data.fileName}
          className="w-full h-auto object-contain"
          style={{ 
            maxWidth: '100%',
            height: 'auto'
          }}
        />
      </div>
      {!isPreview && (
        <>
          <div className="flex items-center gap-2">
            <ColorSwatchPicker
              compact
              color={accentColor}
              fallback={DEFAULT_ACCENT_COLOR}
              onChange={(hex) => onChange({ ...data, accentColor: hex })}
            />
            <span className="text-sm font-bold text-gray-700">สีธีมแถบเครื่องมือ</span>
          </div>
          <div className="flex items-center justify-between text-sm p-3 rounded-lg" style={{ color: accentColor, backgroundColor: hexToRgba(accentColor, 0.08) }}>
            <span className="flex items-center gap-2">
              <Maximize2 className="w-4 h-4" />
              {data.isUrl && <Link className="w-4 h-4 text-blue-500" />}
              {data.fileName || 'รูปภาพขนาดจริง'}
            </span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowUrlInput(true)}
                className="flex items-center gap-2"
                style={{ color: accentColor }}
              >
                <Link className="w-4 h-4" />
                เปลี่ยนเป็น URL
              </button>
              <label className="cursor-pointer flex items-center gap-2" style={{ color: accentColor }}>
                <Upload className="w-4 h-4" />
                อัปโหลดใหม่
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  disabled={uploading}
                />
              </label>
            </div>
          </div>
          {showUrlInput && (
            <div className="flex gap-2 mt-2">
              <input
                type="url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent text-sm"
                style={{ borderColor: hexToRgba(accentColor, 0.5) }}
                onKeyPress={(e) => e.key === 'Enter' && handleUrlSubmit()}
              />
              <button
                onClick={handleUrlSubmit}
                className="px-4 py-2 text-white rounded-lg transition-colors text-sm"
                style={{ backgroundColor: accentColor }}
              >
                เปลี่ยน
              </button>
              <button
                onClick={() => {
                  setShowUrlInput(false);
                  setImageUrl('');
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
              >
                ยกเลิก
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RealSizeImageBlock;
